var _excluded = ["headings", "bulkActions", "promotedBulkActions", "children", "emptyState", "sort", "paginatedSelectAllActionText", "lastColumnSticky", "sortable", "sortDirection", "defaultSortDirection", "sortColumnIndex", "onSort", "sortToggleLabels", "hasZebraStriping"],
    _excluded2 = ["children", "selectable", "itemCount", "selectedItemsCount", "resourceName", "loading", "hasMoreItems", "condensed", "onSelectionChange"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

function _slicedToArray(arr, i) { return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest(); }

function _nonIterableRest() { throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }

function _unsupportedIterableToArray(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen); }

function _arrayLikeToArray(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) { arr2[i] = arr[i]; } return arr2; }

function _iterableToArrayLimit(arr, i) { var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"]; if (_i == null) return; var _arr = []; var _n = true; var _d = false; var _s, _e; try { for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) { _arr.push(_s.value); if (i && _arr.length === i) break; } } catch (err) { _d = true; _e = err; } finally { try { if (!_n && _i["return"] != null) _i["return"](); } finally { if (_d) throw _e; } } return _arr; }

function _arrayWithHoles(arr) { if (Array.isArray(arr)) return arr; }

function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }

function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }

import React, { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import { SortAscendingMajor, SortDescendingMajor } from '@shopify/polaris-icons';
import { CSSTransition } from 'react-transition-group';
import { toPx, tokens, motion } from '@shopify/polaris-tokens';
import { debounce } from '../../utilities/debounce.js';
import { useToggle } from '../../utilities/use-toggle.js';
import { useIsomorphicLayoutEffect } from '../../utilities/use-isomorphic-layout-effect.js';
import { classNames } from '../../utilities/css.js';
import styles from './IndexTable.scss.js';
import { IndexProvider } from '../IndexProvider/IndexProvider.js';
import { Cell } from './components/Cell/Cell.js';
import { Row } from './components/Row/Row.js';
import { useIsBulkActionsSticky } from '../BulkActions/hooks/use-is-bulk-actions-sticky.js';
import { SELECT_ALL_ITEMS, SelectionType } from '../../utilities/index-provider/types.js';
import { getTableHeadingsBySelector } from './utilities/utilities.js';
import { EmptySearchResult } from '../EmptySearchResult/EmptySearchResult.js';
import { ScrollContainer } from './components/ScrollContainer/ScrollContainer.js';
import { BulkActions } from '../BulkActions/BulkActions.js';
import { SelectAllActions } from '../SelectAllActions/SelectAllActions.js';
import { useIndexValue, useIndexSelectionChange } from '../../utilities/index-provider/hooks.js';
import { useI18n } from '../../utilities/i18n/hooks.js';
import { LegacyStack } from '../LegacyStack/LegacyStack.js';
import { Checkbox } from '../Checkbox/Checkbox.js';
import { Spinner } from '../Spinner/Spinner.js';
import { AfterInitialMount } from '../AfterInitialMount/AfterInitialMount.js';
import { EventListener } from '../EventListener/EventListener.js';
import { Badge } from '../Badge/Badge.js';
import { Text } from '../Text/Text.js';
import { UnstyledButton } from '../UnstyledButton/UnstyledButton.js';
import { Tooltip } from '../Tooltip/Tooltip.js';
import { Sticky } from '../Sticky/Sticky.js';
var SCROLL_BAR_PADDING = 4;
var SCROLL_BAR_DEBOUNCE_PERIOD = 300;

function IndexTableBase(_ref) {
  var headings = _ref.headings,
      _ref$bulkActions = _ref.bulkActions,
      bulkActions = _ref$bulkActions === void 0 ? [] : _ref$bulkActions,
      _ref$promotedBulkActi = _ref.promotedBulkActions,
      promotedBulkActions = _ref$promotedBulkActi === void 0 ? [] : _ref$promotedBulkActi,
      children = _ref.children,
      emptyState = _ref.emptyState,
      sort = _ref.sort,
      paginatedSelectAllActionText = _ref.paginatedSelectAllActionText,
      _ref$lastColumnSticky = _ref.lastColumnSticky,
      lastColumnSticky = _ref$lastColumnSticky === void 0 ? false : _ref$lastColumnSticky,
      sortable = _ref.sortable,
      sortDirection = _ref.sortDirection,
      _ref$defaultSortDirec = _ref.defaultSortDirection,
      defaultSortDirection = _ref$defaultSortDirec === void 0 ? 'descending' : _ref$defaultSortDirec,
      sortColumnIndex = _ref.sortColumnIndex,
      onSort = _ref.onSort,
      sortToggleLabels = _ref.sortToggleLabels,
      hasZebraStriping = _ref.hasZebraStriping,
      restProps = _objectWithoutProperties(_ref, _excluded);

  var _useIndexValue = useIndexValue(),
      loading = _useIndexValue.loading,
      bulkSelectState = _useIndexValue.bulkSelectState,
      resourceName = _useIndexValue.resourceName,
      bulkActionsAccessibilityLabel = _useIndexValue.bulkActionsAccessibilityLabel,
      selectMode = _useIndexValue.selectMode,
      _useIndexValue$select = _useIndexValue.selectable,
      selectable = _useIndexValue$select === void 0 ? restProps.selectable : _useIndexValue$select,
      paginatedSelectAllText = _useIndexValue.paginatedSelectAllText,
      itemCount = _useIndexValue.itemCount,
      hasMoreItems = _useIndexValue.hasMoreItems,
      selectedItemsCount = _useIndexValue.selectedItemsCount,
      condensed = _useIndexValue.condensed;

  var handleSelectionChange = useIndexSelectionChange();
  var i18n = useI18n();

  var _useToggle = useToggle(false),
      hasMoreLeftColumns = _useToggle.value,
      toggleHasMoreLeftColumns = _useToggle.toggle;

  var tablePosition = useRef({
    top: 0,
    left: 0
  });
  var tableHeadingRects = useRef([]);
  var scrollableContainerElement = useRef(null);
  var tableElement = useRef(null);
  var condensedListElement = useRef(null);
  var loadingElement = useRef(null);

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      tableInitialized = _useState2[0],
      setTableInitialized = _useState2[1];

  var _useState3 = useState(null),
      _useState4 = _slicedToArray(_useState3, 2),
      stickyWrapper = _useState4[0],
      setStickyWrapper = _useState4[1];

  var _useState5 = useState(false),
      _useState6 = _slicedToArray(_useState5, 2),
      hideScrollContainer = _useState6[0],
      setHideScrollContainer = _useState6[1];

  var tableHeadings = useRef([]);
  var stickyTableHeadings = useRef([]);
  var stickyHeaderWrapperElement = useRef(null);
  var firstStickyHeaderElement = useRef(null);
  var stickyHeaderElement = useRef(null);
  var scrollBarElement = useRef(null);
  var scrollContainerElement = useRef(null);
  var scrollingWithBar = useRef(false);
  var scrollingContainer = useRef(false);
  var lastSortedColumnIndex = useRef(sortColumnIndex);
  var renderAfterSelectEvent = useRef(false);
  var lastSelectedItemsCount = useRef(0);
  var hasSelected = useRef(false);

  if (selectedItemsCount !== lastSelectedItemsCount.current) {
    renderAfterSelectEvent.current = true;
    lastSelectedItemsCount.current = selectedItemsCount;
  }

  if (!hasSelected.current && selectedItemsCount !== 0) {
    hasSelected.current = true;
  }

  var _useIsBulkActionsStic = useIsBulkActionsSticky(selectMode),
      bulkActionsIntersectionRef = _useIsBulkActionsStic.bulkActionsIntersectionRef,
      tableMeasurerRef = _useIsBulkActionsStic.tableMeasurerRef,
      isBulkActionsSticky = _useIsBulkActionsStic.isBulkActionsSticky,
      bulkActionsAbsoluteOffset = _useIsBulkActionsStic.bulkActionsAbsoluteOffset,
      bulkActionsMaxWidth = _useIsBulkActionsStic.bulkActionsMaxWidth,
      bulkActionsOffsetLeft = _useIsBulkActionsStic.bulkActionsOffsetLeft,
      computeTableDimensions = _useIsBulkActionsStic.computeTableDimensions;

  useEffect(function () {
    computeTableDimensions();
  }, [computeTableDimensions, itemCount]);
  var tableBodyRef = useCallback(function (node) {
    if (node !== null && !tableInitialized) {
      setTableInitialized(true);
    }
  }, [tableInitialized]);
  var handleSelectAllItemsInStore = useCallback(function () {
    handleSelectionChange(selectedItemsCount === SELECT_ALL_ITEMS ? SelectionType.Page : SelectionType.All, true);
  }, [handleSelectionChange, selectedItemsCount]);
  var calculateFirstHeaderOffset = useCallback(function () {
    if (!selectable) {
      return tableHeadingRects.current[0].offsetWidth;
    }

    return condensed ? tableHeadingRects.current[0].offsetWidth : tableHeadingRects.current[0].offsetWidth + tableHeadingRects.current[1].offsetWidth;
  }, [condensed, selectable]);
  var resizeTableHeadings = useMemo(function () {
    return debounce(function () {
      if (!tableElement.current || !scrollableContainerElement.current) {
        return;
      }

      var boundingRect = scrollableContainerElement.current.getBoundingClientRect();
      tablePosition.current = {
        top: boundingRect.top,
        left: boundingRect.left
      };
      tableHeadingRects.current = tableHeadings.current.map(function (heading) {
        return {
          offsetWidth: heading.offsetWidth || 0,
          offsetLeft: heading.offsetLeft || 0
        };
      });

      if (tableHeadings.current.length === 0) {
        return;
      } // update left offset for first column


      if (selectable && tableHeadings.current.length > 1) tableHeadings.current[1].style.left = "".concat(tableHeadingRects.current[0].offsetWidth, "px"); // update the min width of the checkbox to be the be the un-padded width of the first heading

      if (selectable && firstStickyHeaderElement !== null && firstStickyHeaderElement !== void 0 && firstStickyHeaderElement.current) {
        var elementStyle = getComputedStyle(tableHeadings.current[0]);
        var boxWidth = tableHeadings.current[0].offsetWidth;
        firstStickyHeaderElement.current.style.minWidth = "calc(".concat(boxWidth, "px - ").concat(elementStyle.paddingLeft, " - ").concat(elementStyle.paddingRight, " + 2px)");
      } // update sticky header min-widths


      stickyTableHeadings.current.forEach(function (heading, index) {
        var minWidth = 0;

        if (index === 0 && (!isBreakpointsXS() || !selectable)) {
          minWidth = calculateFirstHeaderOffset();
        } else if (selectable && tableHeadingRects.current.length > index) {
          var _tableHeadingRects$cu;

          minWidth = ((_tableHeadingRects$cu = tableHeadingRects.current[index]) === null || _tableHeadingRects$cu === void 0 ? void 0 : _tableHeadingRects$cu.offsetWidth) || 0;
        } else if (!selectable && tableHeadingRects.current.length >= index) {
          var _tableHeadingRects$cu2;

          minWidth = ((_tableHeadingRects$cu2 = tableHeadingRects.current[index - 1]) === null || _tableHeadingRects$cu2 === void 0 ? void 0 : _tableHeadingRects$cu2.offsetWidth) || 0;
        }

        heading.style.minWidth = "".concat(minWidth, "px");
      });
    });
  }, [calculateFirstHeaderOffset, selectable]);
  var resizeTableScrollBar = useCallback(function () {
    if (scrollBarElement.current && tableElement.current && tableInitialized) {
      var _scrollContainerEleme, _tableElement$current;

      scrollBarElement.current.style.setProperty('--pc-index-table-scroll-bar-content-width', "".concat(tableElement.current.offsetWidth - SCROLL_BAR_PADDING, "px"));
      setHideScrollContainer(((_scrollContainerEleme = scrollContainerElement.current) === null || _scrollContainerEleme === void 0 ? void 0 : _scrollContainerEleme.offsetWidth) === ((_tableElement$current = tableElement.current) === null || _tableElement$current === void 0 ? void 0 : _tableElement$current.offsetWidth));
    }
  }, [tableInitialized]); // eslint-disable-next-line react-hooks/exhaustive-deps

  var debounceResizeTableScrollbar = useCallback(debounce(resizeTableScrollBar, SCROLL_BAR_DEBOUNCE_PERIOD, {
    trailing: true
  }), [resizeTableScrollBar]);

  var _useState7 = useState(true),
      _useState8 = _slicedToArray(_useState7, 2),
      canScrollRight = _useState8[0],
      setCanScrollRight = _useState8[1]; // eslint-disable-next-line react-hooks/exhaustive-deps


  var handleCanScrollRight = useCallback(debounce(function () {
    if (!lastColumnSticky || !tableElement.current || !scrollableContainerElement.current) {
      return;
    }

    var tableRect = tableElement.current.getBoundingClientRect();
    var scrollableRect = scrollableContainerElement.current.getBoundingClientRect();
    setCanScrollRight(tableRect.width > scrollableRect.width);
  }), [lastColumnSticky]);
  useEffect(function () {
    handleCanScrollRight();
  }, [handleCanScrollRight]);

  var _useState9 = useState(true),
      _useState10 = _slicedToArray(_useState9, 2),
      canFitStickyColumn = _useState10[0],
      setCanFitStickyColumn = _useState10[1];

  var handleCanFitStickyColumn = useCallback(function () {
    if (!scrollableContainerElement.current || !tableHeadings.current.length) {
      return;
    }

    var scrollableRect = scrollableContainerElement.current.getBoundingClientRect();
    var checkboxColumnWidth = selectable ? tableHeadings.current[0].getBoundingClientRect().width : 0;
    var firstStickyColumnWidth = tableHeadings.current[selectable ? 1 : 0].getBoundingClientRect().width;
    var lastColumnIsNotTheFirst = selectable ? tableHeadings.current.length > 2 : 1; // Don't consider the last column in the calculations if it's not sticky

    var lastStickyColumnWidth = lastColumnSticky && lastColumnIsNotTheFirst ? tableHeadings.current[tableHeadings.current.length - 1].getBoundingClientRect().width : 0; // Secure some space for the remaining columns to be visible

    var restOfContentMinWidth = 100;
    setCanFitStickyColumn(scrollableRect.width > firstStickyColumnWidth + checkboxColumnWidth + lastStickyColumnWidth + restOfContentMinWidth);
  }, [lastColumnSticky, selectable]);
  useEffect(function () {
    if (tableInitialized) {
      handleCanFitStickyColumn();
    }
  }, [handleCanFitStickyColumn, tableInitialized]);
  var handleResize = useCallback(function () {
    var _scrollBarElement$cur; // hide the scrollbar when resizing


    (_scrollBarElement$cur = scrollBarElement.current) === null || _scrollBarElement$cur === void 0 ? void 0 : _scrollBarElement$cur.style.setProperty('--pc-index-table-scroll-bar-content-width', "0px");
    resizeTableHeadings();
    debounceResizeTableScrollbar();
    handleCanScrollRight();
    handleCanFitStickyColumn();
  }, [resizeTableHeadings, debounceResizeTableScrollbar, handleCanScrollRight, handleCanFitStickyColumn]);
  var handleScrollContainerScroll = useCallback(function (canScrollLeft, canScrollRight) {
    if (!scrollableContainerElement.current || !scrollBarElement.current) {
      return;
    }

    if (!scrollingWithBar.current) {
      scrollingContainer.current = true;
      scrollBarElement.current.scrollLeft = scrollableContainerElement.current.scrollLeft;
    }

    scrollingWithBar.current = false;

    if (stickyHeaderElement.current) {
      stickyHeaderElement.current.scrollLeft = scrollableContainerElement.current.scrollLeft;
    }

    if (canScrollLeft && !hasMoreLeftColumns || !canScrollLeft && hasMoreLeftColumns) {
      toggleHasMoreLeftColumns();
    }

    setCanScrollRight(canScrollRight);
  }, [hasMoreLeftColumns, toggleHasMoreLeftColumns]);
  var handleScrollBarScroll = useCallback(function () {
    if (!scrollableContainerElement.current || !scrollBarElement.current) {
      return;
    }

    if (!scrollingContainer.current) {
      scrollingWithBar.current = true;
      scrollableContainerElement.current.scrollLeft = scrollBarElement.current.scrollLeft;
    }

    scrollingContainer.current = false;
  }, []);
  useIsomorphicLayoutEffect(function () {
    tableHeadings.current = getTableHeadingsBySelector(tableElement.current, '[data-index-table-heading]');
    stickyTableHeadings.current = getTableHeadingsBySelector(stickyHeaderWrapperElement.current, '[data-index-table-sticky-heading]');
    resizeTableHeadings();
  }, [headings, resizeTableHeadings, firstStickyHeaderElement, tableInitialized]);
  useEffect(function () {
    resizeTableScrollBar();
    setStickyWrapper(condensed ? condensedListElement.current : tableElement.current);
  }, [tableInitialized, resizeTableScrollBar, condensed]);
  var hasBulkActions = Boolean(promotedBulkActions && promotedBulkActions.length > 0 || bulkActions && bulkActions.length > 0);
  var headingsMarkup = headings.map(renderHeading).reduce(function (acc, heading) {
    return acc.concat(heading);
  }, []);
  var bulkActionsSelectable = Boolean(promotedBulkActions.length > 0 || bulkActions.length > 0);
  var stickyColumnHeaderStyle = tableHeadingRects.current && tableHeadingRects.current.length > 0 ? {
    minWidth: calculateFirstHeaderOffset()
  } : undefined;
  var stickyColumnHeader = /*#__PURE__*/React.createElement("div", {
    className: styles.TableHeading,
    key: getHeadingKey(headings[0]),
    style: stickyColumnHeaderStyle,
    "data-index-table-sticky-heading": true
  }, /*#__PURE__*/React.createElement(LegacyStack, {
    spacing: "none",
    wrap: false,
    alignment: "center"
  }, selectable && /*#__PURE__*/React.createElement("div", {
    className: styles.FirstStickyHeaderElement,
    ref: firstStickyHeaderElement
  }, renderCheckboxContent()), selectable && /*#__PURE__*/React.createElement("div", {
    className: styles['StickyTableHeading-second-scrolling']
  }, renderHeadingContent(headings[0], 0)), !selectable && /*#__PURE__*/React.createElement("div", {
    className: styles.FirstStickyHeaderElement,
    ref: firstStickyHeaderElement
  }, renderHeadingContent(headings[0], 0))));
  var stickyHeadingsMarkup = headings.map(renderStickyHeading);
  var selectedItemsCountLabel = selectedItemsCount === SELECT_ALL_ITEMS ? "".concat(itemCount, "+") : selectedItemsCount;
  var handleTogglePage = useCallback(function () {
    handleSelectionChange(SelectionType.Page, Boolean(!bulkSelectState || bulkSelectState === 'indeterminate'));
  }, [bulkSelectState, handleSelectionChange]);
  var paginatedSelectAllAction = getPaginatedSelectAllAction();
  var loadingTransitionClassNames = {
    enter: styles['LoadingContainer-enter'],
    enterActive: styles['LoadingContainer-enter-active'],
    exit: styles['LoadingContainer-exit'],
    exitActive: styles['LoadingContainer-exit-active']
  };
  var loadingMarkup = /*#__PURE__*/React.createElement(CSSTransition, {
    in: loading,
    classNames: loadingTransitionClassNames,
    timeout: parseInt(motion['duration-100'], 10),
    nodeRef: loadingElement,
    appear: true,
    unmountOnExit: true
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.LoadingPanel,
    ref: loadingElement
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.LoadingPanelRow
  }, /*#__PURE__*/React.createElement(Spinner, {
    size: "small"
  }), /*#__PURE__*/React.createElement("span", {
    className: styles.LoadingPanelText
  }, i18n.translate('Polaris.IndexTable.resourceLoadingAccessibilityLabel', {
    resourceNamePlural: resourceName.plural.toLocaleLowerCase()
  })))));
  var stickyTableClassNames = classNames(styles.StickyTable, condensed && styles['StickyTable-condensed']);
  var shouldShowBulkActions = bulkActionsSelectable && selectedItemsCount;
  var bulkActionClassNames = classNames(styles.BulkActionsWrapper, isBulkActionsSticky && styles.BulkActionsWrapperSticky);
  var shouldShowActions = !condensed || selectedItemsCount;
  var promotedActions = shouldShowActions ? promotedBulkActions : [];
  var actions = shouldShowActions ? bulkActions : [];
  var bulkActionsMarkup = shouldShowBulkActions && !condensed ? /*#__PURE__*/React.createElement("div", {
    className: bulkActionClassNames,
    style: {
      insetBlockStart: isBulkActionsSticky ? undefined : bulkActionsAbsoluteOffset,
      width: bulkActionsMaxWidth,
      insetInlineStart: isBulkActionsSticky ? bulkActionsOffsetLeft : undefined
    }
  }, /*#__PURE__*/React.createElement(BulkActions, {
    selectMode: selectMode,
    promotedActions: promotedActions,
    actions: actions,
    onSelectModeToggle: condensed ? handleSelectModeToggle : undefined,
    isSticky: isBulkActionsSticky,
    width: bulkActionsMaxWidth
  })) : null;
  var stickyHeaderMarkup = /*#__PURE__*/React.createElement("div", {
    className: stickyTableClassNames,
    role: "presentation"
  }, /*#__PURE__*/React.createElement(Sticky, {
    boundingElement: stickyWrapper
  }, function (isSticky) {
    var stickyHeaderClassNames = classNames(styles.StickyTableHeader, isSticky && styles['StickyTableHeader-isSticky']);
    var selectAllActionsClassName = classNames(styles.SelectAllActionsWrapper, condensed && styles['StickyTableHeader-condensed'], isSticky && styles['StickyTableHeader-isSticky']);
    var selectAllActionsMarkup = shouldShowBulkActions && !condensed ? /*#__PURE__*/React.createElement("div", {
      className: selectAllActionsClassName
    }, /*#__PURE__*/React.createElement(SelectAllActions, {
      label: i18n.translate('Polaris.IndexTable.selected', {
        selectedItemsCount: selectedItemsCountLabel
      }),
      accessibilityLabel: bulkActionsAccessibilityLabel,
      selected: bulkSelectState,
      selectMode: selectMode,
      onToggleAll: handleTogglePage,
      paginatedSelectAllText: paginatedSelectAllText,
      paginatedSelectAllAction: paginatedSelectAllAction
    }), loadingMarkup) : null;
    var headerMarkup = condensed ? /*#__PURE__*/React.createElement("div", {
      className: classNames(styles.HeaderWrapper, (!selectable || condensed) && styles.unselectable)
    }, loadingMarkup, sort) : /*#__PURE__*/React.createElement("div", {
      className: stickyHeaderClassNames,
      ref: stickyHeaderWrapperElement
    }, loadingMarkup, /*#__PURE__*/React.createElement("div", {
      className: styles.StickyTableColumnHeader
    }, stickyColumnHeader), /*#__PURE__*/React.createElement("div", {
      className: styles.StickyTableHeadings,
      ref: stickyHeaderElement
    }, stickyHeadingsMarkup));
    var stickyContent = selectAllActionsMarkup !== null && selectAllActionsMarkup !== void 0 ? selectAllActionsMarkup : headerMarkup;
    return stickyContent;
  }), bulkActionsMarkup);
  var scrollBarWrapperClassNames = classNames(styles.ScrollBarContainer, condensed && styles.scrollBarContainerCondensed, hideScrollContainer && styles.scrollBarContainerHidden);
  var scrollBarClassNames = classNames(tableElement.current && tableInitialized && styles.ScrollBarContent);
  var scrollBarMarkup = itemCount > 0 ? /*#__PURE__*/React.createElement(AfterInitialMount, {
    onMount: resizeTableScrollBar
  }, /*#__PURE__*/React.createElement("div", {
    className: scrollBarWrapperClassNames,
    ref: scrollContainerElement
  }, /*#__PURE__*/React.createElement("div", {
    onScroll: handleScrollBarScroll,
    className: styles.ScrollBar,
    ref: scrollBarElement
  }, /*#__PURE__*/React.createElement("div", {
    className: scrollBarClassNames
  })))) : null;
  var isSortable = sortable === null || sortable === void 0 ? void 0 : sortable.some(function (value) {
    return value;
  });
  var tableClassNames = classNames(styles.Table, hasMoreLeftColumns && styles['Table-scrolling'], selectMode && styles.disableTextSelection, selectMode && shouldShowBulkActions && styles.selectMode, !selectable && styles['Table-unselectable'], canFitStickyColumn && styles['Table-sticky'], isSortable && styles['Table-sortable'], canFitStickyColumn && lastColumnSticky && styles['Table-sticky-last'], canFitStickyColumn && lastColumnSticky && canScrollRight && styles['Table-sticky-scrolling'], hasZebraStriping && styles.ZebraStriping);
  var emptyStateMarkup = emptyState ? emptyState : /*#__PURE__*/React.createElement(EmptySearchResult, {
    title: i18n.translate('Polaris.IndexTable.emptySearchTitle', {
      resourceNamePlural: resourceName.plural
    }),
    description: i18n.translate('Polaris.IndexTable.emptySearchDescription'),
    withIllustration: true
  });
  var sharedMarkup = /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(EventListener, {
    event: "resize",
    handler: handleResize
  }), /*#__PURE__*/React.createElement(AfterInitialMount, null, stickyHeaderMarkup));
  var condensedClassNames = classNames(styles.CondensedList, hasZebraStriping && styles.ZebraStriping);
  var bodyMarkup = condensed ? /*#__PURE__*/React.createElement(React.Fragment, null, sharedMarkup, /*#__PURE__*/React.createElement("ul", {
    "data-selectmode": Boolean(selectMode),
    className: condensedClassNames,
    ref: condensedListElement
  }, children)) : /*#__PURE__*/React.createElement(React.Fragment, null, sharedMarkup, /*#__PURE__*/React.createElement(ScrollContainer, {
    scrollableContainerRef: scrollableContainerElement,
    onScroll: handleScrollContainerScroll
  }, /*#__PURE__*/React.createElement("table", {
    ref: tableElement,
    className: tableClassNames
  }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", {
    className: styles.HeadingRow
  }, headingsMarkup)), /*#__PURE__*/React.createElement("tbody", {
    ref: tableBodyRef
  }, children))));
  var tableContentMarkup = itemCount > 0 ? bodyMarkup : /*#__PURE__*/React.createElement("div", {
    className: styles.EmptySearchResultWrapper
  }, emptyStateMarkup);
  var tableWrapperClassNames = classNames(styles.IndexTableWrapper, Boolean(bulkActionsMarkup) && selectMode && styles.IndexTableWrapperWithBulkActions);
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: styles.IndexTable
  }, /*#__PURE__*/React.createElement("div", {
    className: tableWrapperClassNames,
    ref: tableMeasurerRef
  }, !shouldShowBulkActions && !condensed && loadingMarkup, tableContentMarkup), /*#__PURE__*/React.createElement("div", {
    ref: bulkActionsIntersectionRef
  })), scrollBarMarkup);

  function renderHeading(heading, index) {
    var isSecond = index === 0;
    var isLast = index === headings.length - 1;
    var hasSortable = sortable === null || sortable === void 0 ? void 0 : sortable.some(function (value) {
      return value === true;
    });
    var headingAlignment = heading.alignment || 'start';
    var headingContentClassName = classNames(styles.TableHeading, headingAlignment === 'center' && styles['TableHeading-align-center'], headingAlignment === 'end' && styles['TableHeading-align-end'], hasSortable && styles['TableHeading-sortable'], isSecond && styles['TableHeading-second'], isLast && !heading.hidden && styles['TableHeading-last'], !selectable && styles['TableHeading-unselectable'], heading.flush && styles['TableHeading-flush']);
    var stickyPositioningStyle = selectable !== false && isSecond && tableHeadingRects.current && tableHeadingRects.current.length > 0 ? {
      left: tableHeadingRects.current[0].offsetWidth
    } : undefined;
    var headingContent = /*#__PURE__*/React.createElement("th", {
      className: headingContentClassName,
      key: getHeadingKey(heading),
      "data-index-table-heading": true,
      style: stickyPositioningStyle
    }, renderHeadingContent(heading, index));

    if (index !== 0 || !selectable) {
      return headingContent;
    }

    var checkboxClassName = classNames(styles.TableHeading, hasSortable && styles['TableHeading-sortable'], index === 0 && styles['TableHeading-first']);
    var checkboxContent = /*#__PURE__*/React.createElement("th", {
      className: checkboxClassName,
      key: "".concat(heading, "-").concat(index),
      "data-index-table-heading": true
    }, renderCheckboxContent());
    return [checkboxContent, headingContent];
  }

  function renderCheckboxContent() {
    return /*#__PURE__*/React.createElement("div", {
      className: styles.ColumnHeaderCheckboxWrapper
    }, /*#__PURE__*/React.createElement(Checkbox, {
      label: i18n.translate('Polaris.IndexTable.selectAllLabel', {
        resourceNamePlural: resourceName.plural
      }),
      labelHidden: true,
      onChange: handleSelectPage,
      checked: bulkSelectState
    }));
  }

  function handleSortHeadingClick(index, direction) {
    renderAfterSelectEvent.current = false;
    hasSelected.current = false;
    lastSortedColumnIndex.current = sortColumnIndex;
    onSort === null || onSort === void 0 ? void 0 : onSort(index, direction);
  }

  function renderHeadingContent(heading, index) {
    var _heading$tooltipWidth;

    var headingContent;
    var defaultTooltipProps = {
      width: (_heading$tooltipWidth = heading.tooltipWidth) !== null && _heading$tooltipWidth !== void 0 ? _heading$tooltipWidth : 'default',
      activatorWrapper: 'div',
      dismissOnMouseOut: true,
      persistOnClick: heading.tooltipPersistsOnClick
    };

    var defaultHeaderTooltipProps = _objectSpread(_objectSpread({}, defaultTooltipProps), {}, {
      padding: '4',
      borderRadius: '2',
      content: heading.tooltipContent,
      preferredPosition: 'above'
    });

    if (heading.new) {
      headingContent = /*#__PURE__*/React.createElement(LegacyStack, {
        wrap: false,
        alignment: "center"
      }, /*#__PURE__*/React.createElement("span", null, heading.title), /*#__PURE__*/React.createElement(Badge, {
        status: "new"
      }, i18n.translate('Polaris.IndexTable.onboardingBadgeText')));
    } else if (heading.hidden) {
      headingContent = /*#__PURE__*/React.createElement(Text, {
        as: "span",
        visuallyHidden: true
      }, heading.title);
    } else {
      headingContent = heading.title;
    }

    if (sortable !== null && sortable !== void 0 && sortable[index]) {
      var _heading$defaultSortD;

      var isCurrentlySorted = index === sortColumnIndex;
      var isPreviouslySorted = !isCurrentlySorted && index === lastSortedColumnIndex.current;
      var isRenderAfterSelectEvent = renderAfterSelectEvent.current || !hasSelected.current && selectedItemsCount !== 0;
      var isAscending = sortDirection === 'ascending';
      var newDirection = (_heading$defaultSortD = heading.defaultSortDirection) !== null && _heading$defaultSortD !== void 0 ? _heading$defaultSortD : defaultSortDirection;
      var SourceComponent = newDirection === 'ascending' ? SortAscendingMajor : SortDescendingMajor;

      if (isCurrentlySorted) {
        newDirection = isAscending ? 'descending' : 'ascending';
        SourceComponent = sortDirection === 'ascending' ? SortAscendingMajor : SortDescendingMajor;
      }

      var iconMarkup = /*#__PURE__*/React.createElement("span", {
        className: classNames(styles.TableHeadingSortIcon, (heading === null || heading === void 0 ? void 0 : heading.alignment) === 'end' && styles['TableHeadingSortIcon-heading-align-end'], isCurrentlySorted && styles['TableHeadingSortIcon-visible'])
      }, /*#__PURE__*/React.createElement(SourceComponent, {
        focusable: "false",
        "aria-hidden": "true",
        className: styles.TableHeadingSortSvg
      }));
      var defaultSortButtonProps = {
        onClick: function onClick() {
          return handleSortHeadingClick(index, newDirection);
        },
        className: classNames(styles.TableHeadingSortButton, !isCurrentlySorted && (heading === null || heading === void 0 ? void 0 : heading.alignment) === 'end' && styles['TableHeadingSortButton-heading-align-end'], isCurrentlySorted && (heading === null || heading === void 0 ? void 0 : heading.alignment) === 'end' && styles['TableHeadingSortButton-heading-align-end-currently-sorted'], isPreviouslySorted && !isRenderAfterSelectEvent && (heading === null || heading === void 0 ? void 0 : heading.alignment) === 'end' && styles['TableHeadingSortButton-heading-align-end-previously-sorted']),
        tabIndex: selectMode ? -1 : 0
      };
      var sortMarkup = /*#__PURE__*/React.createElement(UnstyledButton, defaultSortButtonProps, iconMarkup, /*#__PURE__*/React.createElement("span", {
        className: classNames(sortToggleLabels && selectMode && heading.tooltipContent && styles.TableHeadingTooltipUnderlinePlaceholder)
      }, headingContent));

      if (!sortToggleLabels || selectMode) {
        return /*#__PURE__*/React.createElement("div", {
          className: styles.SortableTableHeadingWithCustomMarkup
        }, sortMarkup);
      }

      var tooltipDirection = isCurrentlySorted ? sortDirection : newDirection;
      var sortTooltipContent = sortToggleLabels[index][tooltipDirection];

      if (!heading.tooltipContent) {
        return (
          /*#__PURE__*/
          // Regular header with sort icon and sort direction tooltip
          React.createElement(Tooltip, Object.assign({}, defaultTooltipProps, {
            content: sortTooltipContent,
            preferredPosition: "above"
          }), sortMarkup)
        );
      }

      if (heading.tooltipContent) {
        return (
          /*#__PURE__*/
          // Header text and sort icon have separate tooltips
          React.createElement("div", {
            className: styles.SortableTableHeadingWithCustomMarkup
          }, /*#__PURE__*/React.createElement(UnstyledButton, defaultSortButtonProps, /*#__PURE__*/React.createElement(Tooltip, defaultHeaderTooltipProps, /*#__PURE__*/React.createElement("span", {
            className: styles.TableHeadingUnderline
          }, headingContent)), /*#__PURE__*/React.createElement(Tooltip, Object.assign({}, defaultTooltipProps, {
            content: sortTooltipContent,
            preferredPosition: "above"
          }), iconMarkup)))
        );
      }
    }

    if (heading.tooltipContent) {
      return (
        /*#__PURE__*/
        // Non-sortable header with tooltip
        React.createElement(Tooltip, Object.assign({}, defaultHeaderTooltipProps, {
          activatorWrapper: "span"
        }), /*#__PURE__*/React.createElement("span", {
          className: classNames(styles.TableHeadingUnderline, styles.SortableTableHeaderWrapper)
        }, headingContent))
      );
    }

    return headingContent;
  }

  function handleSelectPage(checked) {
    handleSelectionChange(SelectionType.Page, checked);
  }

  function renderStickyHeading(heading, index) {
    var position = index + 1;
    var headingStyle = tableHeadingRects.current && tableHeadingRects.current.length > position ? {
      minWidth: tableHeadingRects.current[position].offsetWidth
    } : undefined;
    var headingAlignment = heading.alignment || 'start';
    var headingContent = renderHeadingContent(heading, index);
    var stickyHeadingClassName = classNames(styles.TableHeading, headingAlignment === 'center' && styles['TableHeading-align-center'], headingAlignment === 'end' && styles['TableHeading-align-end'], index === 0 && styles['StickyTableHeading-second'], index === 0 && !selectable && styles.unselectable);
    return /*#__PURE__*/React.createElement("div", {
      className: stickyHeadingClassName,
      key: getHeadingKey(heading),
      style: headingStyle,
      "data-index-table-sticky-heading": true
    }, headingContent);
  }

  function getPaginatedSelectAllAction() {
    if (!selectable || !hasBulkActions || !hasMoreItems) {
      return;
    }

    var customActionText = paginatedSelectAllActionText !== null && paginatedSelectAllActionText !== void 0 ? paginatedSelectAllActionText : i18n.translate('Polaris.IndexTable.selectAllItems', {
      itemsLength: itemCount,
      resourceNamePlural: resourceName.plural.toLocaleLowerCase()
    });
    var actionText = selectedItemsCount === SELECT_ALL_ITEMS ? i18n.translate('Polaris.IndexTable.undo') : customActionText;
    return {
      content: actionText,
      onAction: handleSelectAllItemsInStore
    };
  }

  function handleSelectModeToggle() {
    handleSelectionChange(SelectionType.All, false);
  }
}

var isBreakpointsXS = function isBreakpointsXS() {
  var _toPx;

  return typeof window === 'undefined' ? false : window.innerWidth < parseFloat((_toPx = toPx(tokens.breakpoints['breakpoints-sm'])) !== null && _toPx !== void 0 ? _toPx : '');
};

function getHeadingKey(heading) {
  if ('id' in heading && heading.id) {
    return heading.id;
  }

  if (typeof heading.title === 'string') {
    return heading.title;
  }

  return '';
}

function IndexTable(_ref2) {
  var children = _ref2.children,
      _ref2$selectable = _ref2.selectable,
      selectable = _ref2$selectable === void 0 ? true : _ref2$selectable,
      itemCount = _ref2.itemCount,
      _ref2$selectedItemsCo = _ref2.selectedItemsCount,
      selectedItemsCount = _ref2$selectedItemsCo === void 0 ? 0 : _ref2$selectedItemsCo,
      passedResourceName = _ref2.resourceName,
      loading = _ref2.loading,
      hasMoreItems = _ref2.hasMoreItems,
      condensed = _ref2.condensed,
      onSelectionChange = _ref2.onSelectionChange,
      indexTableBaseProps = _objectWithoutProperties(_ref2, _excluded2);

  return /*#__PURE__*/React.createElement(IndexProvider, {
    selectable: selectable && !condensed,
    itemCount: itemCount,
    selectedItemsCount: selectedItemsCount,
    resourceName: passedResourceName,
    loading: loading,
    hasMoreItems: hasMoreItems,
    condensed: condensed,
    onSelectionChange: onSelectionChange
  }, /*#__PURE__*/React.createElement(IndexTableBase, indexTableBaseProps, children));
}

IndexTable.Cell = Cell;
IndexTable.Row = Row;
export { IndexTable };